// `z` kommt aus `@hono/zod-openapi`: das Modul erweitert Zod beim Laden um `.openapi()`. Schemas, die vor
// dieser Erweiterung entstehen, lassen sich nicht in der OpenAPI-Registry registrieren (Ladereihenfolge).
import { z } from '@hono/zod-openapi';
import { Problem, type ProblemCode as ProblemCodeSchema, problemType } from '@socialfy/contracts';

/** Gemeinsamer Fehlercode ([V] E-p1). */
export type ProblemCode = z.infer<typeof ProblemCodeSchema>;

/** Medientyp der Fehlerantworten (RFC 9457, [V] E-p1). */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';

/** HTTP-Status je gemeinsamem Fehlercode ([V] E-p1); Vollständigkeit über `ProblemCode` erzwungen. */
export const PROBLEM_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  validation: 400,
  conflict: 409,
  demo_read_only: 403,
  rate_limited: 429,
  gone: 410,
  unprocessable: 422,
  unavailable: 503,
  action_disabled: 403,
  internal: 500,
  payload_too_large: 413,
} as const satisfies Record<ProblemCode, number>;

/** Deutscher Standardtitel je Code (Klartext für Laien, ohne Jargon). */
export const PROBLEM_TITLES = {
  unauthorized: 'Nicht angemeldet',
  forbidden: 'Zugriff verweigert',
  not_found: 'Nicht gefunden',
  validation: 'Ungültige Eingabe',
  conflict: 'Konflikt mit dem aktuellen Stand',
  demo_read_only: 'Im Demo-Modus nicht möglich',
  rate_limited: 'Zu viele Anfragen',
  gone: 'Nicht mehr verfügbar',
  unprocessable: 'Nicht verarbeitbar',
  unavailable: 'Vorübergehend nicht verfügbar',
  action_disabled: 'Aktion ist deaktiviert',
  internal: 'Interner Fehler',
  payload_too_large: 'Anfrage zu groß',
} as const satisfies Record<ProblemCode, string>;

/** Modulspezifischer `reason`: Kleinbuchstaben, Ziffern, Unterstrich; beginnt mit einem Buchstaben. */
export const PROBLEM_REASON_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Wire-Schema der Fehlerantwort. Trägt als einzige Stelle die OpenAPI-ID `Problem` (F10: `.meta({ id })`
 * nur einmal je Schema; `contracts` vergibt keine ID) und schärft `reason` auf das Muster.
 */
export const ProblemSchema = Problem.extend({
  reason: z.string().regex(PROBLEM_REASON_PATTERN).optional(),
}).meta({ id: 'Problem' });

type ProblemBase = { requestId: string; reason?: string; detail?: string };
export type ProblemInput =
  | (ProblemBase & {
      code: 'validation';
      fieldErrors?: Record<string, string[]>;
    })
  | (ProblemBase & { code: Exclude<ProblemCode, 'validation'> });

/** Baut eine gültige Fehlerantwort; `fieldErrors` gibt es nur bei `validation`. */
export function problem(input: ProblemInput): Problem {
  const { code, requestId, reason, detail } = input;
  if (reason !== undefined && !PROBLEM_REASON_PATTERN.test(reason)) {
    throw new TypeError(`reason "${reason}" entspricht nicht ${PROBLEM_REASON_PATTERN}`);
  }
  const fieldErrors = 'fieldErrors' in input ? input.fieldErrors : undefined;
  if (fieldErrors !== undefined && code !== 'validation') {
    throw new TypeError('fieldErrors sind nur bei code "validation" zulässig');
  }
  return {
    type: problemType(code),
    title: PROBLEM_TITLES[code],
    status: PROBLEM_STATUS[code],
    code,
    requestId,
    ...(reason === undefined ? {} : { reason }),
    ...(detail === undefined ? {} : { detail }),
    ...(fieldErrors === undefined ? {} : { fieldErrors }),
  };
}
